import React, { useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import ViewYellowCardLink from 'components/HealthRecords/Immunizations/ViewYellowCardLink';
import CalendarUtils from 'services/CalendarUtils';
import './style.scss';
import { setFormValues, clearForm } from 'hooks/useReduxForm';
import TaskEditModal from 'components/Tasks/Utils/TaskEditModal';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import DefaultChip from 'components/Utils/DefaultChip';
import {
  HISTORY_TYPES,
  ISSUE_TYPES,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import Tooltip2 from 'components/Common/Tooltip2';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import PerformanceUtils from 'services/PerformanceUtils';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { PregnancyCalculatorUtils } from 'components/Modals/PregnancyCalculatorModal';
import { IVFCalculatorUtils } from 'components/Modals/IVFCalculatorModal';
import { CalculatorModal } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteSections';
import FRSCalculatorModal, { FRSCalculatorUtils } from 'components/Modals/FRSCalculatorModal';
import PREVENTCalculatorModal, {
  PREVENTCalculatorUtils,
} from 'components/Modals/PREVENTCalculatorModal';

export const HISTORY_ICONS = {
  [HISTORY_TYPES.ethnicity_history]: 'users-rays',
  [HISTORY_TYPES.family_history]: 'family',
  [HISTORY_TYPES.surgical_history]: 'user-doctor',
};

const HISTORY_PATHS = {
  [HISTORY_TYPES.ethnicity_history]: 'ethnicity',
  [HISTORY_TYPES.family_history]: 'family',
  [HISTORY_TYPES.surgical_history]: 'surgical',
};

export const HistoryCPPListElement = ({ patient, bubble, items, setActiveCPPTab, type }) => {
  const separatedItems = useMemo(() => {
    let byType = {};
    items?.forEach((item) => {
      if (CPPUtils.isHealthRecordItemEnabled(item)) {
        let key = item.value.history_type;
        byType[key] = byType[key] || [];
        byType[key].push(item);
      }
    });
    return byType;
  }, [items]);

  return (
    <CPPListElementWrapped
      key={type}
      patient={patient}
      bubble={bubble}
      title={type}
      field="history"
      type={type}
      icon={HISTORY_ICONS[type]}
      items={separatedItems[type]}
      setActiveCPPTab={(activeTab) => {
        setActiveCPPTab(activeTab + '/' + HISTORY_PATHS[type]);
      }}
    />
  );
};

const CPPListElement = (props) => {
  const dispatch = useAppDispatch();
  const ownRole = useAppSelector((state) => state.authentication.role);
  const [generating, setGenerating] = useState(false);
  const [selectedTask, setSelectedTask] = useState();

  const formName = useMemo(
    () => CPPUtils.getFormName(props.field, props.patient.id, props.type),
    [props.field, props.patient.id, props.type],
  );
  const formStack = useAppSelector((state) => state.rhfForms[formName]?.stack);

  const items = useMemo(() => {
    return (props.items || []).filter((item) => CPPUtils.isHealthRecordItemEnabled(item));
  }, [props.items]);

  let allowEdit =
    props.allowEdit !== undefined
      ? props.allowEdit
      : ownRole === 'physician' || ownRole === 'admin';

  // Called from header icon/title — opens a clean empty form
  function openNewItem() {
    if (props.field === 'tasks') {
      setSelectedTask({});
    } else {
      if (!allowEdit) return;
      dispatch(clearForm(formName));
      props.setActiveCPPTab(props.field);
    }
  }

  // Called from item edit clicks (after setFormValues is already dispatched)
  function setActiveCPPTab() {
    if (props.field === 'tasks') {
      setSelectedTask({});
    } else {
      if (!allowEdit) return;
      props.setActiveCPPTab(props.field);
    }
  }

  async function onStartImmunizationSchedule() {
    setGenerating(true);
    await dispatch(api.HealthRecords.generateImmunizations({ patientId: props.patient.id }), {
      errorTitle: 'Generate Scheduled Immunizations',
    });
    setGenerating(false);
  }

  return (
    <div
      className={`cpp-small-box list-element ${props.field} ${props.type || ''} ${
        props.collapse ? 'collapse-list' : ''
      }`}
    >
      {props.field === 'preventative_screenings'}
      {selectedTask && (
        <TaskEditModal
          key={!!selectedTask}
          editModal={!!selectedTask}
          task={selectedTask}
          openEditModal={() => setSelectedTask()}
          showPatient={true}
          patient={props.patient}
        />
      )}
      {!!props.title && (
        <div className="top-info row">
          <Icon
            src={props.icon}
            onClick={openNewItem}
            width="20px"
            height="20px"
            alt={props.title}
          />
          <span className="small-title uppercase" onClick={openNewItem}>
            {props.title}
          </span>
          {!!props.badge && (
            <div>
              <DefaultChip text={'' + props.badge} style={{ marginLeft: '10px' }} />
            </div>
          )}
          {props.field === 'immunizations' && (
            <div className="ml-2 inline-flex" style={{ position: 'relative', top: '-3px' }}>
              <ConfirmationIcon
                src="calendar-clock"
                width="15px"
                alt="Start Immunization Schedule"
                showTooltip={true}
                loading={generating}
                onClick={onStartImmunizationSchedule}
                confirmText="Are you sure you want to generate all future immunizations for this patient?"
              />
            </div>
          )}
        </div>
      )}

      <div className="list-content">
        {props.collapse ? (
          <UncontrolledCollapse initialValue={props.collapseInitialValue}>
            <Content
              {...props}
              items={items}
              setSelectedTask={setSelectedTask}
              allowEdit={allowEdit}
              setActiveCPPTab={setActiveCPPTab}
              formName={formName}
              formStack={formStack}
            />
          </UncontrolledCollapse>
        ) : (
          <Content
            {...props}
            items={items}
            setSelectedTask={setSelectedTask}
            allowEdit={allowEdit}
            setActiveCPPTab={setActiveCPPTab}
            formName={formName}
            formStack={formStack}
          />
        )}
        {props.field === 'administered_drugs' && (
          <div className="list">
            <ViewYellowCardLink
              key="yellow-card"
              text="View History"
              administeredDrugs={true}
              patient={props.patient}
              className="list-element truncate clickable"
            />
          </div>
        )}
        {props.footer}
      </div>
    </div>
  );
};

const Content = ({
  items,
  setActiveCPPTab,
  setSelectedTask,
  allowEdit,
  formName,
  formStack,
  ...props
}) => {
  return (
    <div className="list">
      {props.field !== 'medical_issues' && (
        <List
          {...props}
          setSelectedTask={setSelectedTask}
          field={props.field}
          allowEdit={allowEdit}
          patient={props.patient}
          items={items}
          setActiveCPPTab={setActiveCPPTab}
          groupByName={props.field === 'administered_drugs'}
          formName={formName}
          formStack={formStack}
        />
      )}
      {props.field === 'medical_issues' && (
        <IssuesList
          {...props}
          items={items}
          field={props.field}
          allowEdit={allowEdit}
          patient={props.patient}
          setActiveCPPTab={setActiveCPPTab}
          formName={formName}
          formStack={formStack}
        />
      )}
    </div>
  );
};

export const GTPALList = ({ gtpal, information, ...props }) => {
  const numberGTPALValues = useMemo(() => {
    return CPPUtils.getNumberOfGTPALValues(information?.gtpal) + (gtpal || []).length;
  }, [gtpal, information]);
  const GTPALName = CPPUtils.getGTPAL(information?.gtpal);
  return numberGTPALValues > 0 ? (
    <div className={`cpp-small-box list-element gtpal`}>
      <div className="top-info row">
        <Icon src="baby-carriage" width="20px" height="20px" alt="GTPAL" />
        <span className="small-title uppercase">GTPAL</span>
      </div>
      <div className="list-content">
        <div className="list">
          {GTPALName && (
            <List
              key="information-gtpal-list"
              items={[{ value: { name: GTPALName } }]}
              {...props}
              allowEdit={false}
            />
          )}
          {gtpal?.length > 0 && (
            <List key="gtpal-list" items={gtpal} {...props} allowEdit={false} />
          )}
        </div>
      </div>
    </div>
  ) : null;
};

const IssuesList = ({ items, ...props }) => {
  let newItems = {};

  (items || []).forEach((item) => {
    let issueType = item.value.issue_type || ISSUE_TYPES.current;
    newItems[issueType] = newItems[issueType] || [];
    newItems[issueType].push(item);
  });

  let results = [];

  let sections = Object.keys(newItems);
  sections.sort((a, b) => (a === ISSUE_TYPES.current ? -1 : 1));

  sections.forEach((key) => {
    results = [
      ...results,
      <span className="small-title list-element animated fadeIn" key={key + 'title'}>
        {key}
      </span>,
      <List key={key + 'list'} items={newItems[key]} {...props} />,
    ];
  });

  return results;
};

const List = ({
  items,
  field,
  allowEdit,
  patient,
  groupByName = false,
  formName,
  formStack,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const [selectedDocument, setSelectedDocument] = useState();
  const allDocuments = useAppSelector((state) => state.documents.byId[patient.id]);
  const [downloadingDocuments, setDownloadingDocuments] = useState(false);
  const [frsEditPayload, setFrsEditPayload] = useState(null);
  const [preventEditPayload, setPreventEditPayload] = useState(null);

  let property = 'name';

  const preparedItems = useMemo(() => {
    if (!groupByName) return items;

    return _(items)
      .map((item) => {
        let newItem = PerformanceUtils.clone(item);
        newItem.value.name = newItem.value.name.trim();
        return newItem;
      })
      .groupBy('value.name')
      .map((group) => {
        const sortedGroup = _.sortBy(group, ['value.date', 'value.given_date']).reverse();
        const mainItem = sortedGroup[0];
        const history = _.sortBy(
          sortedGroup.filter((item) => item.value.date || item.value.given_date),
          ['value.date', 'value.given_date'],
        ).reverse();

        return {
          ...mainItem,
          value: {
            ...mainItem.value,
          },
          history: history.map((item) => ({
            date: item.value.date || item.value.given_date,
            action: 'Previous entry',
            value: item.value,
          })),
        };
      })
      .value();
  }, [items, groupByName]);

  useEffect(() => {
    if (downloadingDocuments || field !== 'preventative_screenings') {
      return;
    }

    const toDownload = _(items)
      .map('value.document_id')
      .filter((doc_id) => doc_id && !allDocuments?.find((doc) => doc.id === doc_id))
      .value();

    if (toDownload.length === 0) {
      return;
    }

    setDownloadingDocuments(true);

    const promise = Promise.all(
      toDownload.map((documentId) => dispatch(api.Documents.getDocument({ documentId }))),
    );

    promise.finally(() => {
      setDownloadingDocuments(false);
    });
  }, [allDocuments, field, items]);

  function prepareItem(item) {
    let data;
    if (field === 'tasks') {
      data = {
        itemId: item.id,
        ...item,
      };
    } else {
      data = {
        itemId: item.id,
        ...item.value,
      };
    }
    return data;
  }

  if (field === 'tasks') {
    property = 'title';
  }

  const elements = preparedItems
    ?.map((item) => {
      const clickedItem = () => {
        if (!allowEdit) return;

        if (field === 'tasks' && !!props.setSelectedTask) return props.setSelectedTask(item);

        // FRS notes: open the calculator pre-filled with this entry's
        // snapshot rather than the generic free-form note editor.
        if (field === 'notes' && item.value?.name === FRSCalculatorUtils.NAME) {
          const parsed = FRSCalculatorUtils.tryParseFRS(item.value.notes);
          if (parsed) {
            setFrsEditPayload(parsed);
            return;
          }
        }

        if (field === 'notes' && item.value?.name === PREVENTCalculatorUtils.NAME) {
          const parsed = PREVENTCalculatorUtils.tryParsePREVENT(item.value.notes);
          if (parsed) {
            setPreventEditPayload(parsed);
            return;
          }
        }

        let itemValue = prepareItem(item);

        let stack = formStack;
        dispatch(setFormValues({ formName, values: { ...itemValue, stack } }));
        props.setActiveCPPTab();
      };

      let icons = [];

      if (props.icons) {
        icons = [...props.icons(item, props)];
      }

      if (allowEdit) {
        icons.push(
          <Icon
            key={item.id + '-icon'}
            src="pencil"
            alt="Edit"
            className="edit-icon"
            onClick={clickedItem}
          />,
        );
      }

      const associatedDocument = !!item.value?.document_id
        ? allDocuments?.find((it) => it.id === item.value.document_id)
        : undefined;

      if (!!item.value?.document_id && associatedDocument) {
        icons.push(
          <Icon
            key={item.id + '-pdf-icon'}
            src="file-pdf"
            alt="Open Document"
            className="pdf-icon"
            onClick={() => setSelectedDocument(associatedDocument)}
          />,
        );
      }

      let text = item[property];
      let notes = item.notes;

      if (item.value !== undefined) {
        text = item.value[property];
        notes = item.value.notes;
      }

      if (field === 'history' || field === 'lifestyle_factors') {
        text = item.value.name;
        if (item.value.details) text += ' - ' + item.value.details;
        notes = item.value.notes;
      }

      if (field === 'reminders') {
        text = item.message;
        if (!item.archived) {
          text += ` ${CalendarUtils.fromNow(item.date)}`;
        }
        notes = CalendarUtils.format(item.date);
      }

      if (field === 'consultants') {
        text = item.value.name;
        if (item.value.specialty) text += ' - ' + item.value.specialty;
      }

      if (field === 'notes') {
        if (item.value.name === PregnancyCalculatorUtils.NAME) {
          const decoded = PregnancyCalculatorUtils.decodeForDisplay(item);
          if (!decoded) return;
          text = decoded.text;
          notes = decoded.notes;
        }
        if (item.value.name === IVFCalculatorUtils.NAME) {
          const decoded = IVFCalculatorUtils.decodeForDisplay(item);
          if (!decoded) return;
          text = decoded.text;
          notes = decoded.notes;
        }
        if (item.value.name === FRSCalculatorUtils.NAME) {
          const decoded = FRSCalculatorUtils.decodeForDisplay(item);
          if (!decoded) return;
          text = decoded.text;
          notes = decoded.notes;
        } else if (item.value.name === PREVENTCalculatorUtils.NAME) {
          const decoded = PREVENTCalculatorUtils.decodeForDisplay(item);
          if (!decoded) return;
          text = decoded.text;
          notes = decoded.notes;
        }
      }

      let history = undefined;

      if (item.history && item.history.length > 0) {
        history = item.history.map((historyItem, index) => {
          const key = item.id + '_' + index;
          const datePart = CalendarUtils.format(historyItem.date) + ' ' + historyItem.action + ': ';

          if (field === 'notes' && historyItem.value?.name === FRSCalculatorUtils.NAME) {
            const decoded = FRSCalculatorUtils.decodeNotes(historyItem.value.notes);
            if (decoded) {
              return (
                <span key={key} className="mt-1">
                  {datePart}
                  {decoded.headline}
                  <br />
                  {decoded.detailJsx}
                  <br />
                </span>
              );
            }
          }

          if (field === 'notes' && historyItem.value?.name === PREVENTCalculatorUtils.NAME) {
            const decoded = PREVENTCalculatorUtils.decodeNotes(historyItem.value.notes);
            if (decoded) {
              return (
                <span key={key} className="mt-1">
                  {datePart}
                  {decoded.headline}
                  <br />
                  {decoded.detailJsx}
                  <br />
                </span>
              );
            }
          }

          return (
            <span key={key} className="mt-1">
              {datePart + historyItem.value.name}
              {historyItem.value.details && ` - ${historyItem.value.details}`}
              {field === 'notes' && ` ${historyItem.value.notes}`}
              <br />
            </span>
          );
        });
      }

      if (!notes) notes = undefined;

      let tooltipText = text;

      if (field === 'immunizations') {
        let icon = CPPUtils.getImmunizationIcon(item);
        let alt = CPPUtils.getImmunizationIconDescription(item);
        text = (
          <>
            {icon.src ? (
              <Icon
                {...icon}
                width="15px"
                alt={alt}
                style={{ display: 'inline-block', marginRight: '5px' }}
              />
            ) : null}
            {text}
          </>
        );
        tooltipText = CPPUtils.getImmunizationDescription(item.value);
      }

      if (field === 'administered_drugs') {
        let icon = CPPUtils.getAdministeredDrugIcon(item);
        let alt = CPPUtils.getImmunizationIconDescription(item);
        text = (
          <>
            {icon.src ? (
              <Icon
                {...icon}
                width="15px"
                alt={alt}
                style={{ display: 'inline-block', marginRight: '5px' }}
              />
            ) : null}
            {text}
          </>
        );
        tooltipText = CPPUtils.getImmunizationDescription(item.value);
      }

      if (field === 'tasks') {
        tooltipText = item.details;
      }

      let key = `leftbar-list-${item.id}`;

      const tooltip =
        tooltipText || notes || history ? (
          <TooltipContent tooltipText={tooltipText} notes={notes} history={history} />
        ) : null;

      return (
        <div key={key}>
          <Tooltip2 tooltip={tooltip}>
            {(register) => (
              <span
                className="tooltip-wrapper list-element truncate animated fadeIn"
                id={key}
                {...register}
              >
                {icons ? <div className="list-element-icons">{icons}</div> : null}
                {text}
              </span>
            )}
          </Tooltip2>
        </div>
      );
    })
    .filter((item) => !!item);

  if (field === 'preventative_screenings') {
    elements.push(
      <DocumentDetailsModal
        document={selectedDocument}
        onClose={() => setSelectedDocument()}
        isOpen={!!selectedDocument}
      />,
    );
  }

  if (field === 'immunizations')
    elements.push(
      <ViewYellowCardLink
        key="yellow-card"
        patient={patient}
        className="list-element truncate clickable"
      />,
    );

  if (field === 'notes' && !!frsEditPayload) {
    elements.push(
      <>
        <CalculatorModal
          type="frs"
          name="FRS"
          patient={patient}
          open={!!frsEditPayload}
          setOpen={() => setFrsEditPayload()}
          calculator={<FRSCalculatorModal patient={patient} initialPayload={frsEditPayload} />}
        />
      </>,
    );
  }

  if (field === 'notes' && !!preventEditPayload) {
    elements.push(
      <>
        <CalculatorModal
          type="prevent"
          name="PREVENT"
          patient={patient}
          open={!!preventEditPayload}
          setOpen={() => setPreventEditPayload()}
          calculator={
            <PREVENTCalculatorModal patient={patient} initialPayload={preventEditPayload} />
          }
        />
      </>,
    );
  }

  return elements;
};

const TooltipContent = ({ tooltipText, notes, history }) =>
  tooltipText || notes || history ? (
    <div>
      <span>
        <b>Description</b>
      </span>
      <br />
      {tooltipText}
      {(notes !== undefined || notes === '') && (
        <div>
          <br />
          <span>
            <b>Notes</b>
          </span>
          <br />
          {notes}
        </div>
      )}
      {history !== undefined && (
        <div>
          <br />
          <span>
            <b>History</b>
          </span>
          <br />
          {history}
        </div>
      )}
    </div>
  ) : null;

const CPPListElementWrapped = CPPListElement;

export default CPPListElementWrapped;
